import React, { useState, useEffect, useContext } from 'react';
import { Download, Shield, RefreshCw, Send, AlertTriangle, Activity } from 'lucide-react';
import { SnackbarContext } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../api/api';

export default function Reports() {
    const showSnackbar = useContext(SnackbarContext);

    const [range, setRange] = useState('30d');
    const [activeTab, setActiveTab] = useState('overview');
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const fetchSummary = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/reports/summary?range=${range}`);
            setSummary(res.data);
        } catch (error) {
            handleApiError(error, 'Error fetching report summary');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range]);

    const handleApiError = (error, defaultMsg) => {
        if (error.isAppError) {
            showSnackbar(`${error.code || 'Error'}: ${error.message}`, 'error');
        } else {
            showSnackbar(defaultMsg, 'error');
        }
    };

    const handleExport = async (type) => {
        setExporting(true);
        try {
            const response = await api.get(`/reports/export/${type}?range=${range}`, {
                responseType: 'blob'
            });

            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${type}_export_${range}_${Date.now()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);

            showSnackbar(`${type.toUpperCase()} exported successfully`, 'success');
        } catch (error) {
            // Blob error handling
            if (error.response?.data instanceof Blob) {
                const text = await error.response.data.text();
                try {
                    const json = JSON.parse(text);
                    if (json.error === 'AppError') {
                        showSnackbar(`${json.error}: ${json.message}`, 'error');
                        return;
                    }
                } catch (e) {
                    // text wasn't json
                }
            }
            showSnackbar(`Failed to export ${type}`, 'error');
        } finally {
            setExporting(false);
        }
    };

    const countAgg = (obj) => {
        if (!obj) return 0;
        return Object.values(obj).reduce((a, b) => a + b, 0);
    };

    if (loading && !summary) {
        return <div className="p-8 text-center text-muted-foreground">Generating Reports...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Compliance & Reports</h1>
                    <p className="text-muted-foreground mt-2">
                        System-wide analytics, operational metrics, and evidence-grade exports.
                    </p>
                </div>
                <div className="flex items-center space-x-2">
                    <select
                        value={range}
                        onChange={(e) => setRange(e.target.value)}
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                        <option value="today">Today</option>
                        <option value="7d">Last 7 Days</option>
                        <option value="30d">Last 30 Days</option>
                        <option value="all_time">All Time</option>
                    </select>
                    <Button onClick={fetchSummary} variant="outline" size="sm" disabled={loading}>
                        <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                </div>
            </div>

            {/* KPI Cards */}
            {summary && (
                <div className="grid gap-4 md:grid-cols-4">
                    <div className="rounded-xl border bg-card text-card-foreground shadow p-4 flex flex-col items-center text-center">
                        <Send className="h-6 w-6 mb-2 text-primary" />
                        <h3 className="text-2xl font-bold">{countAgg(summary.campaigns)}</h3>
                        <p className="text-xs text-muted-foreground">Campaign Dispatches</p>
                    </div>
                    <div className="rounded-xl border bg-card text-card-foreground shadow p-4 flex flex-col items-center text-center">
                        <Shield className="h-6 w-6 mb-2 text-green-600" />
                        <h3 className="text-2xl font-bold">{countAgg(summary.approvals)}</h3>
                        <p className="text-xs text-muted-foreground">Governance Approvals</p>
                    </div>
                    <div className="rounded-xl border bg-card text-card-foreground shadow p-4 flex flex-col items-center text-center">
                        <AlertTriangle className="h-6 w-6 mb-2 text-orange-500" />
                        <h3 className="text-2xl font-bold">{countAgg(summary.security)}</h3>
                        <p className="text-xs text-muted-foreground">Security Alerts</p>
                    </div>
                    <div className="rounded-xl border bg-card text-card-foreground shadow p-4 flex flex-col items-center text-center">
                        <Activity className="h-6 w-6 mb-2 text-blue-500" />
                        <h3 className="text-2xl font-bold">{summary.outbox?.DEAD_LETTER || 0}</h3>
                        <p className="text-xs text-muted-foreground">Dead Letters / Fails</p>
                    </div>
                </div>
            )}

            {/* Tab Navigation */}
            <div className="flex space-x-2 border-b">
                <button className={`px-4 py-2 font-medium text-sm ${activeTab === 'overview' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'}`} onClick={() => setActiveTab('overview')}>Overview</button>
                <button className={`px-4 py-2 font-medium text-sm ${activeTab === 'exports' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'}`} onClick={() => setActiveTab('exports')}>Evidence Exports</button>
            </div>

            {/* Tab Content */}
            <div className="bg-card border rounded-lg shadow-sm p-6 min-h-[300px]">

                {activeTab === 'overview' && summary && (
                    <div className="grid grid-cols-2 gap-8">
                        <div>
                            <h3 className="text-lg font-semibold mb-4 border-b pb-2">Top Audit Actions</h3>
                            <ul className="space-y-2">
                                {summary.auditTopActions?.length === 0 && <li className="text-muted-foreground text-sm">No audit logs in this range.</li>}
                                {summary.auditTopActions?.map((a, i) => (
                                    <li key={i} className="flex justify-between items-center text-sm">
                                        <div className="flex items-center">
                                            <Badge variant={a.status === 'SUCCESS' ? 'outline' : 'destructive'} className="mr-2 px-1 text-[10px]">{a.status}</Badge>
                                            <span className="font-mono text-xs">{a.action}</span>
                                        </div>
                                        <span className="font-bold">{a.count}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wider">Campaign Status Breakdown</h3>
                                <div className="flex gap-2 text-sm flex-wrap">
                                    {Object.entries(summary.campaigns || {}).map(([k, v]) => (
                                        <Badge key={k} variant="secondary">{k}: {v}</Badge>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wider">Outbox / Notification Health</h3>
                                <div className="flex gap-2 text-sm flex-wrap">
                                    {Object.entries(summary.outbox || {}).map(([k, v]) => (
                                        <Badge key={k} variant="secondary">{k}: {v}</Badge>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wider">Security Alerts</h3>
                                <div className="flex gap-2 text-sm flex-wrap">
                                    {Object.entries(summary.security || {}).map(([k, v]) => (
                                        <Badge key={k} variant={k === 'OPEN' ? 'destructive' : 'default'}>{k}: {v}</Badge>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'exports' && (
                    <div className="max-w-xl space-y-4">
                        <p className="text-sm text-muted-foreground mb-6">
                            Export historical raw data for compliance, auditing, and investigations.
                            Uses MongoDB streaming buffers to safely extract infinite records without memory crashing.
                            All export requests are strictly logged into the <code>AuditLog</code>.
                        </p>

                        <div className="flex justify-between items-center bg-muted p-4 rounded-lg">
                            <div>
                                <h4 className="font-medium">Audit Logs</h4>
                                <p className="text-xs text-muted-foreground">Complete timeline of authentication, modifications, and system events.</p>
                            </div>
                            <Button onClick={() => handleExport('audit')} disabled={exporting} size="sm">
                                <Download className="mr-2 h-4 w-4" /> Export CSV
                            </Button>
                        </div>

                        <div className="flex justify-between items-center bg-muted p-4 rounded-lg">
                            <div>
                                <h4 className="font-medium">Governance (Approvals)</h4>
                                <p className="text-xs text-muted-foreground">Maker/Checker records, Replays, and High-Risk task executions.</p>
                            </div>
                            <Button onClick={() => handleExport('approvals')} disabled={exporting} size="sm">
                                <Download className="mr-2 h-4 w-4" /> Export CSV
                            </Button>
                        </div>

                        <div className="flex justify-between items-center bg-muted p-4 rounded-lg">
                            <div>
                                <h4 className="font-medium">Security Alerts</h4>
                                <p className="text-xs text-muted-foreground">Rate limit breaches, unauthorized attempts, and detected abuses.</p>
                            </div>
                            <Button onClick={() => handleExport('alerts')} disabled={exporting} size="sm">
                                <Download className="mr-2 h-4 w-4" /> Export CSV
                            </Button>
                        </div>

                        <div className="flex justify-between items-center bg-muted p-4 rounded-lg">
                            <div>
                                <h4 className="font-medium">Outbox (Dead Letters)</h4>
                                <p className="text-xs text-muted-foreground">Raw table of asynchronous tasks, emails, and webhook deliveries.</p>
                            </div>
                            <Button onClick={() => handleExport('outbox')} disabled={exporting} size="sm">
                                <Download className="mr-2 h-4 w-4" /> Export CSV
                            </Button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}
